import React, { type FunctionComponent, useState } from 'react'
import {
  Alert,
  borderRadius,
  Button,
  colors,
  getTargetedPortal,
  LoadingDots,
  notify,
  PageLayout,
  PageLayoutContent,
  PageLayoutFooter,
  Section,
  spacing,
  Text,
} from '@retailer-platform/shared-components'
import { ApprovedIcon } from '@instacart/ids-core'
import styled from '@emotion/styled'
import { StringParam, useQueryParam } from 'use-query-params'
import { FormattedMessage } from 'react-intl'
import { css } from '@emotion/react'
import { useGetProduct } from 'domains/products/components/view/utils/useGetProduct.hooks'
import { type InstacartCustomersSharedV1Locale } from 'domains/products/api'
import { useWarehouseContext } from 'utils/contexts/warehouse/WarehouseContext.hooks'
import { LoadingPage } from 'gin-and-tonic/containers/loading-page/LoadingPage'
import { ProductsEditImagesHeader } from 'domains/products/components/view/components/header/ProductsEditImagesHeader'
import { type Product } from 'domains/products/components/view/utils/types'
import { ImageUploader } from 'gin-and-tonic/containers/image-uploader/ImageUploader'
import clientEnv from 'utils/global/clientEnv'
import { requestUpdate } from 'domains/products/components/edit-images/components/ProductImagesUploader'
import { selectInvalidImageAngles } from 'domains/products/components/edit-images/utils/imageUtils'
import { useDomainGoToPath } from 'domains/products/utils/domain/routing'
import { useDomainMessages } from 'domains/products/utils/domain/intl'
import {
  type EditImagesValues,
  PrimaryImageAngle,
  PrimaryImageFieldName,
  type SecondaryImageAngleMapping,
  SecondaryImageAngles,
  type SecondaryImageAnglesType,
  SecondaryImageDefaults,
} from './components/types/EditImageTypes.types'

const SectionContent = styled.div({
  padding: spacing.X24,
  display: 'flex',
  flexDirection: 'column',
})

const sectionStyles = css({
  width: '720px',
  margin: spacing.X24 + ' auto ' + spacing.X24 + ' auto ',
})

interface ImageUploaderBorderProps {
  error: boolean
}

const ImageUploaderBorder = styled.div<ImageUploaderBorderProps>`
  position: relative;
  height: 150px;
  width: 150px;
  display: flex;
  justify-content: center;
  align-items: top;
  border-color: ${props => (props.error ? colors.DETRIMENTAL.REGULAR : colors.GRAYSCALE.X30)};
  border-width: 1px;
  border-style: solid;
  border-radius: ${borderRadius.X4};
`

const SecondaryImageContent = styled.div({
  padding: spacing.X12,
  display: 'inline-grid',
  width: '25%',
})

const ImageError = styled.span`
  display: inline-block;
  margin-top: ${spacing.X4};
  color: ${colors.DETRIMENTAL.REGULAR};
`

const getEditImagesValues = (product: Product): EditImagesValues => {
  const images: EditImagesValues = {
    primaryImageUrl: null,
    secondaryImages: SecondaryImageDefaults,
  }
  if (product.imageList) {
    product.imageList.map(i => {
      if (SecondaryImageAngles.includes(i.imageType as SecondaryImageAnglesType)) {
        images.secondaryImages[i.imageType] = i.imageUrl
      } else if (i.imageType == PrimaryImageAngle) {
        images.primaryImageUrl = i.imageUrl
      }
    })
  }
  return images
}

const getNewSecondaryImages = (secondaryImages, angle, newUrl): SecondaryImageAngleMapping => {
  const newSecondaryImages = JSON.parse(JSON.stringify(secondaryImages))
  newSecondaryImages[angle] = newUrl
  return newSecondaryImages
}

interface ProductsEditImagesProps {
  productId: string
  retailerId: string
  partnerId: string
}

export const ProductEditImagesView: FunctionComponent<ProductsEditImagesProps> = ({
  productId,
  retailerId,
  partnerId,
}) => {
  const { warehouse } = useWarehouseContext()
  const [locale = warehouse.defaultLocale.localeCode, setLocaleQueryParam] = useQueryParam(
    'locale',
    StringParam
  )

  const { product, loading, error, refetch } = useGetProduct({
    productId,
    retailerId,
    localeCode: locale.toUpperCase() as InstacartCustomersSharedV1Locale,
  })
  const returnToDetails = useDomainGoToPath('products-view-details')
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState({})
  const [alertErrors, setAlertErrors] = useState(null)
  const [productImages, setProductImages] = useState<EditImagesValues>(null)

  const messages = useDomainMessages({
    primaryTitle: 'productsDomain.view.details.sections.images.primaryImages',
    secondaryTitle: 'productsDomain.view.edit-images.secondaryImages',
  })

  if (!loading && productImages == null) {
    setProductImages(getEditImagesValues(product))
  }

  if (loading) return <LoadingPage />

  const [PortalTarget, Portal] = getTargetedPortal()

  const validate = newImages => {
    const initialImages = getEditImagesValues(product)
    const errors = {}
    const imageErrors = selectInvalidImageAngles(initialImages, newImages)

    if (imageErrors.length > 0) {
      imageErrors.forEach(errorAngle => {
        if (errorAngle === PrimaryImageAngle) {
          errors['primaryImageUrl'] = (
            <FormattedMessage id={'productsDomain.view.edit-images.images.errors'} />
          )
        } else if (errors['secondaryImages']) {
          errors['secondaryImages'][errorAngle] = (
            <FormattedMessage id={'productsDomain.view.edit-images.images.errors'} />
          )
        } else {
          errors['secondaryImages'] = {}
          errors['secondaryImages'][errorAngle] = (
            <FormattedMessage id={'productsDomain.view.edit-images.images.errors'} />
          )
        }
      })
      setAlertErrors(<FormattedMessage id={'productsDomain.view.edit-images.images.form.error'} />)
    } else {
      setAlertErrors(null)
    }
    setErrors(errors)
  }

  const onSavingComplete = () => {
    setSaving(false)
    notify({
      contents: (
        <React.Fragment>
          <ApprovedIcon size={15} color="systemSuccessRegular" css={{ marginRight: '5px' }} />
          <FormattedMessage id={'productsDomain.view.edit-images.images.form.updated'} />
        </React.Fragment>
      ),
      timeout: 5000,
    })
    returnToDetails()
  }

  const onSavingError = () => {
    setSaving(false)
    setAlertErrors(
      <FormattedMessage id={'productsDomain.view.edit-images.images.form.saveError'} />
    )
    window.scrollTo(0, 0)
  }

  return (
    <React.Fragment>
      <ProductsEditImagesHeader productName={product.productDisplayName} />
      <PageLayout noSidebar noHeader>
        <PageLayoutContent css={{ backgroundColor: colors.GRAYSCALE.X10 }}>
          <form>
            {dirty && alertErrors && (
              <Alert variant="danger" css={sectionStyles}>
                <Text weight="semibold">{alertErrors}</Text>
              </Alert>
            )}
            {dirty && Object.keys(errors).length == 0 && !alertErrors && (
              <Alert variant="success" css={sectionStyles}>
                <Text weight="semibold">
                  <FormattedMessage id={'productsDomain.view.edit-images.images.form.ready'} />
                </Text>
              </Alert>
            )}
            <Section title={messages.primaryTitle} wrapperStyles={sectionStyles}>
              <SectionContent>
                <ImageUploaderBorder error={errors['primaryImageUrl']}>
                  <ImageUploader
                    compact
                    value={productImages && productImages.primaryImageUrl}
                    onChange={(uploadUrl: string, file: File) => {
                      const newProductImages = { ...productImages, primaryImageUrl: uploadUrl }
                      setProductImages(newProductImages)
                      setDirty(true)
                      validate(newProductImages)
                    }}
                    filestackApiKey={clientEnv.PUBLIC_CLIENT_FILEPICKER_API_KEY}
                  />
                </ImageUploaderBorder>
                {errors['primaryImageUrl'] && (
                  <Text>
                    <ImageError>{errors['primaryImageUrl']}</ImageError>
                  </Text>
                )}
              </SectionContent>
            </Section>
            <Section title={messages.secondaryTitle} wrapperStyles={sectionStyles}>
              <SectionContent style={{ display: 'inline-block' }}>
                {SecondaryImageAngles.map((angle: SecondaryImageAnglesType) => (
                  <SecondaryImageContent key={angle}>
                    <Text size={'small'}>
                      <FormattedMessage id={`catalog.product.angle.${angle}`} />
                    </Text>
                    <div style={{ marginBottom: '6px' }} />
                    <ImageUploaderBorder
                      error={errors['secondaryImages'] && errors['secondaryImages'][angle]}
                    >
                      <ImageUploader
                        compact
                        value={productImages && productImages.secondaryImages[angle]}
                        onChange={(uploadUrl: string, file: File) => {
                          const newProductImages = {
                            ...productImages,
                            secondaryImages: getNewSecondaryImages(
                              productImages.secondaryImages,
                              angle,
                              uploadUrl
                            ),
                          }
                          setProductImages(newProductImages)
                          setDirty(true)
                          validate(newProductImages)
                        }}
                        filestackApiKey={clientEnv.PUBLIC_CLIENT_FILEPICKER_API_KEY}
                      />
                    </ImageUploaderBorder>
                    {errors['secondaryImages'] && errors['secondaryImages'][angle] && (
                      <Text>
                        <ImageError>{errors['secondaryImages'][angle]}</ImageError>
                      </Text>
                    )}
                  </SecondaryImageContent>
                ))}
              </SectionContent>
            </Section>
            <Portal>
              <Text css={{ color: colors.GRAYSCALE.X50 }} size={'medium'} display={'inline-block'}>
                <FormattedMessage id="productsDomain.view.edit-images.images.form.processingTime" />
              </Text>
              <Button onClick={returnToDetails} variant="secondary">
                <FormattedMessage id="common.cancel" />
              </Button>
              <Button
                type="submit"
                disabled={!dirty || saving || Object.keys(errors).length > 0}
                onClick={() => {
                  setSaving(true)
                  requestUpdate(
                    getEditImagesValues(product),
                    productImages,
                    onSavingComplete,
                    onSavingError,
                    retailerId,
                    partnerId,
                    productId
                  )
                }}
                data-testid={'form-confirmation-bar-submit-button'}
              >
                {saving ? (
                  <LoadingDots height={12} />
                ) : (
                  <FormattedMessage id="productsDomain.view.edit-images.images.form.save" />
                )}
              </Button>
              <span style={{ margin: '18px' }} />
            </Portal>
          </form>
        </PageLayoutContent>
        <PageLayoutFooter css={{ justifyContent: 'flex-end' }}>
          <PortalTarget />
        </PageLayoutFooter>
      </PageLayout>
    </React.Fragment>
  )
}
